import { Box, Flex, Text } from '@byecode/ui'
import type { ArgumentsSchema, Field, IndependentAction, RichTextContentProtocol } from '@lighthouse/core'
import type { FlowNode, VariableTree2 } from '@lighthouse/shared'
import {
    defaultFormat,
    getFilterControllerVariableOption,
    getFormDatasourceOptions,
    getImageVariableOption,
    getInputVariableOption,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getViewVariableOption,
    innerTypeNameMap,
    innerTypeToFieldType,
    NodeFieldPreview,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import { useAtomValue } from 'jotai'
import React, { useCallback, useDeferredValue, useMemo } from 'react'

import { availableFieldBlocksAtom, availableFilterBlocksAtom, blockHighlightAtom, lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useCustomViewStatisticExtraFields } from '@/hooks/useCustomViewStatisticExtraFields'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

interface Props {
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    enableSelectDataSource?: boolean
    autoHeight?: boolean
    data: ArgumentsSchema
    highlighting?: boolean
    placeholder?: string
    disabledLabel?: boolean
    value?: RichTextContentProtocol
    onChange?: (value: RichTextContentProtocol) => void
}

export const ArgInput: React.FC<Props> = ({
    data,
    enableSelectDataSource,
    allParentNodes,
    allParentActions,
    placeholder,
    disabledLabel,
    autoHeight,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()

    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomValue(workflowListAtom)
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const { type } = useFlowContainerContext()
    const isInFlow = !!type

    const virtualField: Field | undefined = useMemo(() => {
        const extraConfig =
            data.innerType === 'DATE'
                ? {
                      date: {
                          format: defaultFormat
                      }
                  }
                : {}
        return {
            id: '1',
            name: innerTypeNameMap[data.innerType],
            type: innerTypeToFieldType[data.innerType],
            dsId: '',
            innerType: data.innerType,
            ...extraConfig
        } as Field
    }, [data.innerType])

    // const fieldFilter = useCallback(
    //     (arg: Field | TypeInstanceMap) => (typeof arg === 'object' ? arg.innerType : arg) === data.innerType,
    //     [data.innerType]
    // )

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()
    const upstreamOptions = useMemo(() => {
        if (isInFlow) {
            return getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: allParentNodes,
                allUpstreamNodes: allParentNodes
            })
        }
        return getUpstreamActionsOptions({
            workflowList,
            allParentActions: allParentActions || [],
            dataSourceList
        })
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, workflowList])

    const inputOption = useMemo(() => {
        if (availableFieldBlocksInfo.length === 0 || isInFlow) {
            return
        }
        return getInputVariableOption(availableFieldBlocksInfo, dataSourceList, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFieldBlocksInfo, isInFlow, dataSourceList, handleMouseEnter, handleMouseLeave])

    const filterOption = useMemo(() => {
        if (availableFilterBlocksInfo.length === 0 || isInFlow) {
            return
        }
        return getFilterControllerVariableOption(availableFilterBlocksInfo, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFilterBlocksInfo, handleMouseEnter, handleMouseLeave, isInFlow])

    const { viewDsId = '', formDsId = '', viewType, isImage } = useActionAdderDepParams()
    const formDataSource = useDataSource(appId, envId, formDsId)
    const viewDataSource = useDataSource(appId, envId, viewDsId)

    const formOption = useMemo(() => {
        if (formDataSource && !isInFlow) {
            return getFormDatasourceOptions({ dataSource: formDataSource })
        }
    }, [formDataSource, isInFlow])

    const imageOption = useMemo(() => {
        if (isImage) {
            return getImageVariableOption()
        }
    }, [isImage])

    const { customViewOption } = useVariableCustomViewOption()
    const extraViewFields = useCustomViewStatisticExtraFields()

    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }

        const options: VariableTree2[] = []
        if (viewDataSource) {
            const viewOption = getViewVariableOption({
                dataSource: viewDataSource,
                viewType,
                extraFields: extraViewFields
            })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }
        return options
    }, [isInFlow, viewDataSource, customViewOption, extraViewFields, viewType])

    return (
        <Flex direction="column" gap={8}>
            {!disabledLabel && (
                <Box>
                    <Text inline size={14} color="var(--color-black)">
                        {data.name}
                    </Text>
                    <Text inline size={14} color="var(--color-gray-400)">
                        [{innerTypeNameMap[data.innerType]}]
                    </Text>
                </Box>
            )}
            <NodeFieldPreview
                innerTypes={[data.innerType]}
                autoHeight={autoHeight}
                placeholder={placeholder}
                field={virtualField}
                viewOptions={viewOptions}
                pageOptions={pageOptions}
                userOption={userOption}
                inputOption={inputOption}
                filterOption={filterOption}
                formOption={formOption}
                imageOption={imageOption}
                upstreamOptions={upstreamOptions}
                dataSourceList={dataSourceList}
                enableSelectDataSource={enableSelectDataSource}
                value={value}
                onBlur={onChange}
            />
        </Flex>
    )
}
